import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Mail, Lock, Eye, EyeOff, Shield, ArrowLeft } from "lucide-react";
import { GustoLogo } from "@/components/GustoLogo";
import GustoLoadingOverlay from "@/components/GustoLoadingOverlay";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { toast } from "sonner";
import { motion } from "framer-motion";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminTaps, setAdminTaps] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Identifiants incorrects");
    } finally {
      setLoading(false);
    }
  };

  // Secret admin access: tap logo 5 times
  const handleLogoTap = () => {
    const newTaps = adminTaps + 1;
    setAdminTaps(newTaps);
    if (newTaps >= 5) {
      setAdminTaps(0);
      setEmail("admin@gusto.fr");
      setPassword("");
      toast("Accès G activé", { icon: "🔐" });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <GustoLoadingOverlay show={loading} message="Connexion en cours..." />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-charcoal mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>
        <div className="text-center mb-8">
          <button
            onClick={handleLogoTap}
            className="inline-flex items-center gap-2 focus:outline-none"
            data-testid="login-logo"
          >
            <GustoLogo size="xl" />
          </button>
          <p className="text-muted-foreground mt-2">Votre assistant culinaire intelligent</p>
        </div>

        <Card className="shadow-lg border-border/50" data-testid="login-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Connexion</CardTitle>
            <CardDescription>Accédez à votre espace cuisine</CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {/* Admin mode indicator */}
              {email === "admin@gusto.fr" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-xl border border-purple-200"
                >
                  <Shield className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-medium text-purple-700">Accès G — Administration</span>
                </motion.div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="chef@cuisine.fr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 rounded-xl"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-12 rounded-xl"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-4">
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 h-12 rounded-full shadow-md"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? "Connexion..." : "Se connecter"}
              </Button>
              <p className="text-sm text-center text-muted-foreground">
                Pas encore de compte ?{" "}
                <Link to="/register" className="text-primary hover:underline font-medium">
                  Créer un compte
                </Link>
              </p>
            </CardFooter>
          </form>
        </Card>
      </motion.div>
    </div>
  );
};

export default LoginPage;
